import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Avatar,
  CircularProgress,
  LinearProgress,
  Divider,
  Tabs,
  Tab,
  TextField,
  Skeleton,
  Popover,
  Breadcrumbs,
  Link
} from '@mui/material';
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { ChevronRight, Clear } from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend
} from 'recharts';
import {
  Calendar as CalendarIcon,
  Bell,
  Search,
  ChevronDown,
  Phone,
  PhoneIncoming,
  PhoneOff,
  Users,
  MessageSquare,
  Activity,
  Filter,
  TrendingUp,
  BarChart2,
} from 'lucide-react';
import SideNavbar from "../../../components/sideNavbar";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import {
  StatCard,
  AccountOverview,
  ChatOperationsSection
} from './DashboardComponents';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// Helper to format seconds into M:SS
const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label, isTime = false }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        bgcolor: '#1F2937',
        color: 'white',
        px: 2,
        py: 1.5,
        borderRadius: '10px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
        fontSize: '12px'
      }}>
        <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '11px' }}>{label}</Typography>
        {payload.map((p, i) => (
          <Box key={i} sx={{ mt: 0.5 }}>
            <Typography variant="body2" fontWeight={700} sx={{ color: p.color || 'white', fontSize: '13px' }}>
              {p.name}: {isTime ? formatDuration(p.value) : p.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

// Chart card wrapper
const ChartCard = ({ title, subtitle, children, height = '380px', hasData = true, emptyLabel = "No data available" }) => (
  <Box sx={{
    bgcolor: 'white',
    p: 3,
    borderRadius: '20px',
    border: '1px solid #F0F0F5',
    height,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
      transform: 'translateY(-1px)',
    }
  }}>
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#111827', fontSize: '15px' }}>{title}</Typography>
      {subtitle && <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '12px' }}>{subtitle}</Typography>}
    </Box>
    <Box sx={{ flexGrow: 1, width: '100%', minHeight: 0, position: 'relative' }}>
      {!hasData ? (
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0.6
        }}>
          <Box sx={{
            p: 2,
            borderRadius: '50%',
            bgcolor: '#F3F4F6',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <BarChart2 size={32} color="#9CA3AF" />
          </Box>
          <Typography variant="body2" fontWeight={600} color="#6B7280">{emptyLabel}</Typography>
          <Typography variant="caption" color="#9CA3AF" sx={{ mt: 0.5 }}>Try adjusting your date filters</Typography>
        </Box>
      ) : children}
    </Box>
  </Box>
);

const CHART_COLORS = ['#4F46E5', '#7C3AED', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'];

/* --- Dashboard Skeleton for Shimmer Effect --- */
const DashboardLoadingSkeleton = () => (
  <Box sx={{ flexGrow: 1, ml: { md: '280px' }, width: '100%' }}>
    <Container maxWidth="xl" sx={{ mt: 3, pb: 8 }}>
      {/* Top Bar Skeleton */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4, pb: 2.5, borderBottom: '1px solid #F0F0F5' }}>
        <Skeleton variant="text" width={200} height={40} animation="wave" />
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="circular" width={40} height={40} animation="wave" />
          <Box>
            <Skeleton variant="text" width={100} animation="wave" />
            <Skeleton variant="text" width={60} animation="wave" />
          </Box>
        </Stack>
      </Stack>

      {/* Overview Skeleton */}
      <Skeleton variant="rectangular" height={220} sx={{ borderRadius: '20px', mb: 4 }} animation="wave" />

      {/* Navigation Tabs Skeleton */}
      <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
        <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: '12px' }} animation="wave" />
        <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: '12px' }} animation="wave" />
      </Stack>

      {/* Grid Stats Skeleton */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} lg={4} key={i}>
            <Skeleton variant="rectangular" height={140} sx={{ borderRadius: '20px' }} animation="wave" />
          </Grid>
        ))}
      </Grid>

      {/* Big Chart Skeleton */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <Skeleton variant="rectangular" height={380} sx={{ borderRadius: '20px' }} animation="wave" />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Skeleton variant="rectangular" height={380} sx={{ borderRadius: '20px' }} animation="wave" />
        </Grid>
      </Grid>

      {/* Triple Charts Row Skeleton */}
      <Grid container spacing={3}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} lg={4} key={i}>
            <Skeleton variant="rectangular" height={340} sx={{ borderRadius: '20px' }} animation="wave" />
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

const DashboardV2 = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filter, setFilter] = useState('all');
  const [leadSource, setLeadSource] = useState('status');
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [calendarAnchor, setCalendarAnchor] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        filter: filter,
      };
      const formatDate = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      if (dateRange.start) params.startDate = formatDate(dateRange.start);
      if (dateRange.end) params.endDate = formatDate(dateRange.end);

      const res = await axiosInstance.get(`${apiEndPointsConfig.getDashboardV2Data}/me`, { params });
      if (res.data.success) {
        setData(res.data.data);
      }
      // console.log(res.data.data.dropoff)
      if (res?.data?.data?.dropoff !== "demo") {
        navigate("/form");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }, [filter, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const defaultDataStructure = {
    stats: {
      totalCalls: { value: '0', trendValue: '+0%', trend: 'up', color: '#4F46E5' },
      lifetimeTotalCalls: { value: '0', color: '#4F46E5' },
      connectedCalls: { value: '0', trendValue: '+0%', trend: 'up', color: '#10B981' },
      notConnected: { value: '0', trendValue: '+0%', trend: 'up', color: '#EF4444' },
      leadStatus: {
        total: { value: '0', color: '#4F46E5' },
        reached: { value: '0', color: '#7C3AED' },
        qualified: { value: '0', color: '#06B6D4' },
        new: { value: '0', color: '#F59E0B' }
      },
      leadPipeline: {
        total: { value: '0', color: '#4F46E5' },
        reached: { value: '0', color: '#7C3AED' },
        qualified: { value: '0', color: '#06B6D4' },
        new: { value: '0', color: '#F59E0B' }
      },
      totalLeads: { value: '0', trendValue: '+0%', trend: 'up', color: '#4F46E5' },
      reachedLeads: { value: '0', trendValue: '+0%', trend: 'up', color: '#7C3AED' },
      qualifiedLeads: { value: '0', trendValue: '+0%', trend: 'up', color: '#06B6D4' },
    },
    charts: {
      callVolumeData: [],
      callDispositionData: [],
      ahtData: [],
      slaData: [],
      peakOpsData: [],
      leadConversionData: [],
      teamData: [],
      inboundOutboundData: []
    },
    didNumber: 'N/A',
    companyName: 'Loading...',
    userName: 'User',
    expiryDate: null
  };

  const dashboardData = data || defaultDataStructure;
  const { stats, charts, didNumber, companyName, userName, expiryDate } = dashboardData;

  return (
    <Box sx={{ display: { xs: 'block', md: 'flex' }, bgcolor: '#F8F9FC', minHeight: '100vh', width: '100%' }}>
      <SideNavbar />

      {loading && !data ? (
        <DashboardLoadingSkeleton />
      ) : (
        <Box sx={{ flexGrow: 1, ml: { md: '280px' }, width: '100%' }}>
          <Container maxWidth="xl" sx={{
            mt: {
              xs: 0,
              md: 3
            }, pb: 8
          }}>

            {/* ── Top Bar ── */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{
                mb: { xs: 0, md: 0 },
                pb: {
                  xs: 1.5,
                  md: 2.5
                },
                // borderBottom: '1px solid #F0F0F5',
                gap: { xs: 2, sm: 0 },
                display: 'flex'
              }}
            >
              <Box sx={{
                position: 'relative', width: { xs: '100%', md: '300px' }, display: {
                  xs: 'none',
                  md: 'block'
                }
              }}>

                <Breadcrumbs
                  separator={<ChevronRight sx={{ fontSize: 14 }} color="disabled" />}
                  sx={{ mb: 1, '& .MuiBreadcrumbs-li': { fontSize: '12px', fontWeight: 500 } }}
                >
                  <Link
                    component={RouterLink}
                    underline="hover"
                    color="#9CA3AF"
                    to="/dashboard"
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    Workspace
                  </Link>
                  <Typography color="#111827" sx={{ fontSize: '12px', fontWeight: 600 }}>Dashboard</Typography>
                </Breadcrumbs>
              </Box>

              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'space-between', sm: 'flex-end' },
                  flexWrap: 'nowrap',
                  gap: 1.5
                }}
              >
                <Button
                  onClick={(e) => setCalendarAnchor(e.currentTarget)}
                  startIcon={<CalendarIcon size={14} style={{ color: '#6B7280' }} />}
                  endIcon={dateRange.start && (
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      setDateRange({ start: null, end: null });
                      setFilter('all');
                    }} sx={{ p: 0 }}>
                      <Clear style={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                  sx={{
                    borderRadius: '14px',
                    bgcolor: 'white',
                    fontSize: '13px',
                    fontWeight: 500,
                    color: '#374151',
                    textTransform: 'none',
                    px: 2,
                    py: 1.2,
                    border: '1px solid #E5E7EB',
                    '&:hover': {
                      bgcolor: '#F9FAFB',
                      borderColor: '#D1D5DB'
                    },
                    minWidth: { xs: '120px', sm: '150px' },
                    justifyContent: 'space-between'
                  }}
                >
                  {dateRange.start ? (
                    `${dateRange.start.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} - ${dateRange.end?.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) || "..."}`
                  ) : "Filter Date"}
                </Button>

                <Popover
                  open={Boolean(calendarAnchor)}
                  anchorEl={calendarAnchor}
                  onClose={() => setCalendarAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  PaperProps={{ sx: { p: 1, mt: 1, borderRadius: '16px', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', border: '1px solid #F3F4F6' } }}
                >
                  <Calendar
                    selectRange
                    onChange={(range) => {
                      if (range && range.length === 2) {
                        setDateRange({ start: range[0], end: range[1] });
                        setFilter('custom'); // Set to custom or handle appropriately
                        setCalendarAnchor(null);
                      }
                    }}
                    value={dateRange.start ? [dateRange.start, dateRange.end] : null}
                  />
                </Popover>

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pl: { xs: 1, sm: 2 }, borderLeft: '1px solid #E5E7EB' }}>
                  <Box textAlign="right" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#111827', fontSize: '13px' }}>{companyName || userName}</Typography>
                    <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '11px' }}>Justtawk Plan</Typography>
                  </Box>
                  <Avatar sx={{
                    bgcolor: '#4F46E5',
                    width: { xs: 32, sm: 38 }, height: { xs: 32, sm: 38 },
                    fontWeight: 700,
                    fontSize: '14px',
                    border: '2px solid #E0E7FF',
                    boxShadow: '0 2px 8px rgba(79, 70, 229, 0.2)'
                  }}>
                    {(companyName || userName || 'U')[0].toUpperCase()}
                  </Avatar>
                  {/* On mobile, show company name after avatar or beside filter */}
                  <Box
                    sx={{
                      display: { xs: 'block', sm: 'none' },
                      maxWidth: '80px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#111827', fontSize: '11px' }}>
                      {companyName || userName}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Stack>

            {/* ── Account Overview ── */}
            <AccountOverview
              companyName={companyName}
              customerCareNo={didNumber}
              expiryDate={expiryDate}
              subscriptionStatus={data?.subscriptionStatus}
              subscriptionStart={data?.subscriptionStart}
            />

            {/* ── Call Stats Row ── */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6} lg={4}>
                <StatCard
                  title="Total Calls"
                  value={filter === 'all' && stats.lifetimeTotalCalls ? stats.lifetimeTotalCalls.value : stats.totalCalls.value}
                  icon={Phone}
                  color={stats.totalCalls.color}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatCard title="Connected Calls" value={stats.connectedCalls.value} icon={PhoneIncoming} color={stats.connectedCalls.color} />
              </Grid>
              <Grid item xs={12} sm={6} lg={4}>
                <StatCard title="Not Connected" value={stats.notConnected.value} icon={PhoneOff} color={stats.notConnected.color} />
              </Grid>
            </Grid>

            {/* ── Lead Source Switcher ── */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 4, mb: 2 }}>
              <Box sx={{ p: 0.5, bgcolor: '#F3F4F6', borderRadius: '14px', display: 'inline-flex' }}>
                {[
                  { label: 'Lead Status', value: 'status' },
                  { label: 'Lead Pipeline', value: 'pipeline' }
                ].map((tab) => (
                  <Box
                    key={tab.value}
                    onClick={() => setLeadSource(tab.value)}
                    sx={{
                      px: 3,
                      py: 1.2,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '13px',
                      color: leadSource === tab.value ? '#111827' : '#6B7280',
                      bgcolor: leadSource === tab.value ? 'white' : 'transparent',
                      boxShadow: leadSource === tab.value ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.25s ease',
                      userSelect: 'none',
                      '&:hover': {
                        color: leadSource === tab.value ? '#111827' : '#374151',
                      }
                    }}
                  >
                    {tab.label}
                  </Box>
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '12px' }}>
                {leadSource === 'status' ? 'Campaign lead metrics' : 'Deal pipeline metrics'}
              </Typography>
            </Stack>

            {/* ── Lead Stats Row ── */}
            <Grid container spacing={3}>
              {leadSource === 'status' ? (
                <>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard title="Total Leads" value={stats.leadStatus?.total?.value || "0"} icon={Users} color="#4F46E5" />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard title="Contacted Leads" value={stats.leadStatus?.reached?.value || "0"} icon={Users} color="#7C3AED" />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard title="Qualified Leads" value={stats.leadStatus?.qualified?.value || "0"} icon={Users} color="#06B6D4" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard title="Total Leads" value={stats.leadPipeline?.total?.value || "0"} icon={Users} color="#4F46E5" />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard title="Contacted Leads" value={stats.leadPipeline?.reached?.value || "0"} icon={Users} color="#7C3AED" />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <StatCard title="Qualified Leads" value={stats.leadPipeline?.qualified?.value || "0"} icon={Users} color="#06B6D4" />
                  </Grid>
                </>
              )}
            </Grid>

            {/* ── Chat Operations ── */}
            <Box sx={{ mt: 4 }}>
              <ChatOperationsSection />
            </Box>

            {/* ── Analytics Deep Dive ── */}
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 6, mb: 3 }}>
              <Box sx={{ p: 1, bgcolor: '#EEF2FF', borderRadius: '10px' }}>
                <Activity size={20} color="#4F46E5" />
              </Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#111827' }}>Analytics Deep Dive</Typography>
            </Stack>

            {/* ── Charts Row 1 ── */}
            <Grid container spacing={3}>
              {/* Call Volume — Gradient Area Chart */}
              <Grid item xs={12} lg={6}>
                <ChartCard
                  title="Call Volume Trends"
                  subtitle="Daily call volume over time"
                  hasData={charts.callVolumeData && charts.callVolumeData.some(d => d.value > 0)}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={charts.callVolumeData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                      <defs>
                        <linearGradient id="callVolumeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#4F46E5" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#4F46E5" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="value" name="Calls" stroke="#4F46E5" strokeWidth={2.5} fillOpacity={1} fill="url(#callVolumeGradient)" dot={{ r: 3, fill: '#4F46E5', strokeWidth: 0 }} activeDot={{ r: 6, fill: '#4F46E5', stroke: 'white', strokeWidth: 2 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Disposition Overview — Progress Bars */}
              <Grid item xs={12} lg={6}>
                <ChartCard
                  title="Disposition Overview"
                  subtitle="Call outcome breakdown"
                  hasData={charts.callDispositionData && charts.callDispositionData.length > 0}
                >
                  <Box sx={{ overflowY: 'auto', pr: 1, height: '100%' }}>
                    <Stack spacing={2.5}>
                      {charts.callDispositionData?.map((item, index) => (
                        <Box key={index}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color || CHART_COLORS[index % CHART_COLORS.length] }} />
                              <Typography variant="body2" fontWeight={600} color="#374151" sx={{ fontSize: '13px' }}>
                                {item.name}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Typography variant="subtitle2" fontWeight={700} color="#111827" sx={{ fontSize: '14px' }}>
                                {item.value}
                              </Typography>
                              <Typography variant="caption" fontWeight={600} sx={{ color: '#9CA3AF', bgcolor: '#F3F4F6', px: 1, py: 0.2, borderRadius: '6px', fontSize: '11px' }}>
                                {item.percentage}%
                              </Typography>
                            </Stack>
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={parseFloat(item.percentage)}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              bgcolor: `${item.color || CHART_COLORS[index % CHART_COLORS.length]}10`,
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                bgcolor: item.color || CHART_COLORS[index % CHART_COLORS.length],
                                transition: 'transform 0.8s ease'
                              }
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </ChartCard>
              </Grid>
            </Grid>

            {/* ── Charts Row 2 ── */}
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {/* AHT Line Chart */}
              <Grid item xs={12} sm={6} lg={4}>
                <ChartCard
                  title="Avg Handle Time (AHT)"
                  subtitle="Actual vs Target (Minutes)"
                  hasData={charts.ahtData && charts.ahtData.length > 0}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={charts.ahtData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                      <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }}
                        tickFormatter={(val) => formatDuration(val)}
                      />
                      <RechartsTooltip content={<CustomTooltip isTime={true} />} />
                      <Line type="monotone" dataKey="target" name="Target" stroke="#E5E7EB" strokeDasharray="6 4" dot={false} strokeWidth={2} />
                      <Line type="monotone" dataKey="actual" name="Actual" stroke="#4F46E5" strokeWidth={2.5} dot={{ r: 4, fill: '#4F46E5', stroke: 'white', strokeWidth: 2 }} activeDot={{ r: 6, fill: '#4F46E5', stroke: 'white', strokeWidth: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Peak Operations */}
              <Grid item xs={12} sm={6} lg={4}>
                <ChartCard
                  title="Peak Operations"
                  subtitle="Calls by hour of day"
                  hasData={charts.peakOpsData && charts.peakOpsData.length > 0}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.peakOpsData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                      <defs>
                        <linearGradient id="peakBarGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#4F46E5" stopOpacity={1} />
                          <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.8} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                      <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9CA3AF', fontWeight: 500 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Calls" fill="url(#peakBarGradient)" radius={[6, 6, 0, 0]} maxBarSize={30} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Lead Qualification */}
              <Grid item xs={12} sm={6} lg={4}>
                <ChartCard
                  title="Lead Qualification"
                  subtitle="Pipeline funnel breakdown"
                  hasData={charts.leadConversionData && charts.leadConversionData.some(d => d.value > 0)}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.leadConversionData} layout="vertical" margin={{ top: 5, right: 10, left: -5, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#6B7280', fontWeight: 500 }} width={80} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Leads" radius={[0, 6, 6, 0]} maxBarSize={24}>
                        {charts.leadConversionData?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            {/* ── Charts Row 3 ── */}
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
              {/* SLA Compliance Trend */}
              <Grid item xs={12} lg={6}>
                <ChartCard
                  title="SLA Compliance Trend"
                  subtitle="% Leads called within 15 mins (Business Hours)"
                  hasData={charts.slaData && charts.slaData.some(d => d.value > 0)}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={charts.slaData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                      <defs>
                        <linearGradient id="slaGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} unit="%" domain={[0, 100]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="value"
                        name="SLA %"
                        stroke="#8B5CF6"
                        strokeWidth={2.5}
                        fillOpacity={1}
                        fill="url(#slaGradient)"
                        dot={{ r: 3, fill: '#8B5CF6', strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: '#8B5CF6', stroke: 'white', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              {/* Inbound vs Outbound */}
              <Grid item xs={12} lg={6}>
                <ChartCard
                  title="Inbound vs Outbound"
                  subtitle="Call direction breakdown by day"
                  hasData={charts.inboundOutboundData && charts.inboundOutboundData.some(d => d.inbound > 0 || d.outbound > 0)}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={charts.inboundOutboundData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
                      <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF', fontWeight: 500 }} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', fontWeight: 500 }} />
                      <Bar dataKey="inbound" name="Inbound" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                      <Bar dataKey="outbound" name="Outbound" fill="#4F46E5" radius={[4, 4, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default DashboardV2;
